import * as React from 'react';
import { cn } from '../utils/cn';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Controlled checked state */
  checked?: boolean;
  /** Default checked state */
  defaultChecked?: boolean;
  /** Change handler */
  onChange?: (e: { target: { checked: boolean } }) => void;
  /** Checkbox label (children) */
  children?: React.ReactNode;
  /** Antd compat: visual shape variant — accepted but not visually differentiated */
  shape?: 'default' | 'circle' | 'circle-outline';
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, defaultChecked, onChange, disabled, children, style, shape: _shape, ...props }, ref) => {
    return (
      <label
        className={cn(
          'inline-flex items-center gap-2 cursor-pointer select-none',
          disabled && 'opacity-40 cursor-not-allowed',
          className
        )}
        style={style}
      >
        <div className="relative flex items-center justify-center">
          <input
            ref={ref}
            type="checkbox"
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange ? (e) => onChange({ target: { checked: e.target.checked } }) : undefined}
            disabled={disabled}
            className={cn(
              'peer h-5 w-5 shrink-0 appearance-none',
              'border-2 border-input bg-white rounded-sm',
              'checked:bg-primary checked:border-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-40',
              'transition-colors duration-fast ease-smooth'
            )}
            {...props}
          />
          {/* Checkmark SVG overlay */}
          <svg
            className="pointer-events-none absolute hidden h-3 w-3 text-white peer-checked:block"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        {children && (
          <span className="text-sm font-body text-foreground">{children}</span>
        )}
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
